import { defineStore } from "pinia";

import type { ErrorObject } from "@vuelidate/core";
import { navigateTo } from "#app";
import { format } from "date-fns";
import type { IOffCatalogRequestStore } from "~/utils/interfaces/requests-interfaces";
import { OffCatalogStep } from "~/utils/enums/catalog.enums";
import { useOffCatalogValidator } from "~/composables/catalog/useOffCatalogValidator";
import { SessionItemModalityEnum } from "~/utils/enums/session-items.enums";
import { RequestsApi, type CreateOffRequestDTO } from "@simbelapp/order-sdk";
import { useFeedback } from "~/composables/feedback/useFeedback";
import { useCreateRequestStore } from "~/store/requests/create-request.store";
import { useTrainingsStore } from "~/store/trainings/trainings.store";
import { useSessionsStore } from "~/store/sessions/sessions-store";
import { useAuthStore } from "~/store/auth/auth.store";
import { useSDKApi } from "~/composables/api/useSDKApi";
import { MicroserviceEnum } from "~/utils/enums/common.enums";

export const useOffCatalogRequests = defineStore("offCatalogRequests", {
  state: (): IOffCatalogRequestStore => {
    return {
      training: {
        trainingPartner: null,
        training: null,
        externalUrl: null,
        durationHours: null,
        format: null,
        price: null,
        inclTax: false,
        modality: SessionItemModalityEnum.SPECIFIC_DATES,
        startDate: null,
        endDate: null,
        employeeComment: null,
      },
      errors: {},
      showModal: false,
      step: OffCatalogStep.AskTrainingDesired,
      isDesktop: true,
    };
  },
  getters: {
    hasError(): boolean {
      return !!Object.keys(this.errors)?.length;
    },
  },
  actions: {
    pushValidationErrors(errors: ErrorObject[]) {
      errors.forEach((err: ErrorObject) => {
        this.errors[err.$propertyPath] = err.$message as string;
      });
    },

    /**
     * Single entry point for the off-catalogue funnel: clears every store the funnel reads.
     *
     * The funnel reuses catalogue components (RequestTrainingPageModal & co) that read
     * `trainingsStore.trainingPageDetails` / `sessionsStore.sessions`. Nothing clears those when
     * the catalogue training panel closes, so a funnel opened right after browsing a training
     * inherited its id and re-fetched its sessions — surfacing a date selector on a request that
     * has no training at all. Each store owns its own cleanup (`clearPageDetails` / `clearSessions`
     * clear only the leaking key, never `$reset()`, which would also drop `trainingsStore
     * .currentUrl` — the return path `catalog/index.vue` branches its modal on — and the cached
     * `trainingCatalog`); this action is only the coordination point.
     */
    openFunnel() {
      const createRequestStore = useCreateRequestStore();
      const trainingsStore = useTrainingsStore();
      const sessionsStore = useSessionsStore();

      this.$reset();
      createRequestStore.$reset();
      trainingsStore.clearPageDetails();
      sessionsStore.clearSessions();
      this.showModal = true;
    },

    async validator() {
      const offCatalogValidator = useOffCatalogValidator();
      this.errors = {};

      if (this.step === OffCatalogStep.WhichTraining) await offCatalogValidator.validateStepTraining();
      if (this.step === OffCatalogStep.UserSpecifyContext) await offCatalogValidator.validateStepEmployeeComment();
    },

    async createOffCatalogRequest() {
      const createRequestStore = useCreateRequestStore();
      const authStore = useAuthStore();
      const feedback = useFeedback();
      const userId = authStore.user?.userId;
      if (userId) {
        try {
          const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, RequestsApi);
          const data = await apiInstance.createOffCatalogRequest({
            // SDK types every field as required; backend accepts null (see legacy IPostCreateOffCatalogRequests).
            createOffRequestDTO: {
              trainingPartnerId: this.training.trainingPartner?.trainingPartnerId ?? null,
              trainingPartnerName: this.training.trainingPartner?.name ?? null,
              trainingId: this.training.training?.trainingId ?? null,
              trainingTitle: this.training.training?.title ?? null,
              modality: this.training.modality,
              format: this.training.format,
              startDate: this.training?.startDate ? format(this.training.startDate, "yyyy-MM-dd") : null,
              endDate: this.training?.endDate ? format(this.training.endDate, "yyyy-MM-dd") : null,
              price: this.training.price,
              durationHours: this.training.durationHours,
              inclTax: this.training.inclTax,
              userIds: createRequestStore.request.usersToInvite.length
                ? createRequestStore.request.usersToInvite.map((u) => u.userId)
                : [userId],
              managerId: createRequestStore.isCheckedForOther ? userId : null,
              externalUrl: this.training.externalUrl,
              managerComment: createRequestStore.request?.managerComment ?? null,
              managerPriority: createRequestStore.request?.managerPriority?.key ?? null,
              employeeComment: this.training.employeeComment,
            } as CreateOffRequestDTO,
          });

          if (data?.requests?.length) {
            feedback.success("Votre demande a bien été enregistrée", "small");
            this.showModal = false;
            if (!createRequestStore.isCheckedForOther) {
              return navigateTo(`my-trainings/${data.requests[0]}`);
            } else {
              return navigateTo(`requests`);
            }
          } else {
            feedback.error("Une erreur est survenue", "small");
          }
        } catch {
          feedback.error("Une erreur est survenue", "small");
        }
      }
    },
  },
});
